import 'react-fancybox/lib/fancybox.css';

import { Chip } from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ColumnDescription } from 'react-bootstrap-table-next';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory, {
  PaginationProvider,
  PaginationTotalStandalone,
  SizePerPageDropdownStandalone,
} from 'react-bootstrap-table2-paginator';
import ToolkitProvider from 'react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit';
import { isMobile } from 'react-device-detect';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Breadcrumb from '@/components/common/breadcrumb.component';
import DebounceSearchBar from '@/components/debounce-search-bar';
import Icon from '@/components/icon';
import NoDataIndicator from '@/components/no-data-indicator';
import StandalonePagination from '@/components/pagination/standalone';
import RowMoreOptions from '@/components/table/row-more-options';
import TableLoader from '@/components/table-loader';
import { Constants } from '@/constants';

type ClaimStatus =
  | 'submitted'
  | 'pending'
  | 'under_review'
  | 'verification_required'
  | 'approved'
  | 'rejected';

interface ClaimVenue {
  id: number;
  hash_id?: string | number | null;
  name: string;
  active?: boolean;
  address?: string | null;
  city?: string | null;
  region?: string | null;
  phone?: string | null;
  email?: string | null;
}

interface ClaimBrand {
  id: number;
  hash_id?: string | number | null;
  name: string;
  active?: boolean;
  website?: string | null;
  email?: string | null;
}

interface ClaimUser {
  id: number;
  hash_id?: string | number | null;
  first_name?: string | null;
  last_name?: string | null;
  display_name?: string | null;
  email?: string | null;
  phone_number?: string | null;
  email_verified?: boolean;
}

interface BusinessClaim {
  id: number;

  venue_id: number | null;
  brand_id: number | null;
  user_id: number | null;

  venue?: ClaimVenue | null;
  brand?: ClaimBrand | null;
  user?: ClaimUser | null;

  status: ClaimStatus | string;
  approved: boolean;

  verification_code: string | null;

  processing_timestamp: string;
  claimed_timestamp: string | null;

  instagram_username: string | null;
  twitter_username: string | null;

  // I recommend changing this to string in the API/model.
  // Phone numbers should not be stored as numeric values.
  contact_number: number | string | null;

  contact_name: string | null;
  contact_email?: string | null;
  owner_name: string | null;
  call_time: string | null;

  email_verified_at?: string | null;
  account_linked_at?: string | null;
  ownership_verified_at?: string | null;
}

interface ClaimsResponse {
  data: BusinessClaim[];
  recordsTotal: number;
}

interface ClaimsTableState {
  page: number;
  sizePerPage: number;
  searchText: string;
  sortField: string;
  sortOrder: 'asc' | 'desc';
  status?: string;
}

const initialTableState: ClaimsTableState = {
  page: 1,
  sizePerPage: 30,
  searchText: '',
  sortField: 'processing_timestamp',
  sortOrder: 'desc',
};

const defaultSorted = [
  {
    dataField: 'processing_timestamp',
    order: 'desc' as const,
  },
];

const getClaimEntity = (claim: BusinessClaim) => {
  if (claim.venue) {
    return {
      type: 'Venue',
      name: claim.venue.name,
      id: claim.venue.id,
      active: claim.venue.active,
    };
  }

  if (claim.brand) {
    return {
      type: 'Brand',
      name: claim.brand.name,
      id: claim.brand.id,
      active: claim.brand.active,
    };
  }

  return {
    type: 'Unmatched',
    name: 'Business not yet staged',
    id: null,
    active: false,
  };
};

const getUserName = (user?: ClaimUser | null) => {
  if (!user) {
    return null;
  }

  if (user.display_name) {
    return user.display_name;
  }

  const fullName = [user.first_name, user.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();

  return fullName || user.email || `User #${user.id}`;
};

const formatDate = (value?: string | null) => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const getStatusLabel = (claim: BusinessClaim) => {
  if (claim.approved || claim.status === 'approved') {
    return 'Approved';
  }

  switch (claim.status) {
    case 'submitted':
      return 'Submitted';
    case 'pending':
      return 'Pending';
    case 'under_review':
      return 'Under Review';
    case 'verification_required':
      return 'Verification Required';
    case 'rejected':
    case 'expired':
      return 'Rejected';
    default:
      return claim.status
        ? claim.status
            .replaceAll('_', ' ')
            .replace(/\b\w/g, (character) => character.toUpperCase())
        : 'Pending';
  }
};

const getStatusChipColor = (
  claim: BusinessClaim
):
  | 'default'
  | 'primary'
  | 'secondary'
  | 'error'
  | 'info'
  | 'success'
  | 'warning' => {
  if (claim.approved || claim.status === 'approved') {
    return 'success';
  }

  switch (claim.status) {
    case 'rejected':
    case 'expired':
      return 'error';
    case 'verification_required':
      return 'warning';
    case 'under_review':
      return 'info';
    case 'submitted':
      return 'primary';
    default:
      return 'default';
  }
};

const ClaimsTable: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [claims, setClaims] = useState<BusinessClaim[]>([]);
  const [totalClaimCount, setTotalClaimCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [actionClaimId, setActionClaimId] = useState<number | null>(null);

  const [tableState, setTableState] = useState<ClaimsTableState>(() => ({
    ...initialTableState,
    searchText: searchParams.get('q') ?? '',
    status: searchParams.get('status') ?? undefined,
  }));

  const getClaims = useCallback(
    async ({
      page,
      sizePerPage,
      searchText,
      sortField,
      sortOrder,
      status,
    }: ClaimsTableState) => {
      setLoading(true);
      setLoadError(false);

      try {
        const response = await axios.get<ClaimsResponse>(
          `${Constants.apiPath}/businesses/datatables/claims`,
          {
            params: {
              page,
              limit: sizePerPage,
              q: searchText || undefined,
              status: status || undefined,
              sort: sortField,
              order: sortOrder,
              include: 'venue,brand,user',
            },
          }
        );
        setClaims(response.data.data);
        setTotalClaimCount(response.data.recordsTotal);
      } catch (error) {
        console.error('Unable to load business claims', error);
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    getClaims(tableState);
  }, [getClaims, tableState]);

  const refreshClaims = useCallback(() => {
    getClaims(tableState);
  }, [getClaims, tableState]);

  const updateClaim = useCallback(
    async (
      claim: BusinessClaim,
      action:
        | 'review'
        | 'verify'
        | 'approve'
        | 'reject'
        | 'create-user'
        | 'resend-verification'
        | 'resend-account-setup'
    ) => {
      setActionClaimId(claim.id);

      try {
        await axios.post(
          `${Constants.apiPath}/business-claims/${claim.id}/${action}`
        );

        refreshClaims();
      } catch (error) {
        console.error(
          `Unable to perform "${action}" for claim ${claim.id}`,
          error
        );
      } finally {
        setActionClaimId(null);
      }
    },
    [refreshClaims]
  );

  const openClaim = useCallback(
    (claim: BusinessClaim) => {
      navigate(`/business-claims/${claim.id}`, {
        state: { claim },
      });
    },
    [navigate]
  );

  const openEntity = useCallback(
    (claim: BusinessClaim) => {
      if (claim.venue) {
        navigate(`/venues/editor/${claim.venue.hash_id ?? claim.venue.id}`, {
          state: { venue: claim.venue },
        });

        return;
      }

      if (claim.brand) {
        navigate(`/brands/editor/${claim.brand.hash_id ?? claim.brand.id}`, {
          state: { brand: claim.brand },
        });
      }
    },
    [navigate]
  );

  const actionDropdown = useCallback(
    (index: number, claim: BusinessClaim) => {
      const hasUser = Boolean(claim.user_id || claim.user);
      const hasEntity = Boolean(
        claim.venue_id || claim.brand_id || claim.venue || claim.brand
      );
      const isApproved = claim.approved || claim.status === 'approved';
      const isRejected =
        claim.status === 'rejected' || claim.status === 'expired';
      const actionPending = actionClaimId === claim.id;

      const items: any[] = [
        {
          type: 'MenuItem',
          onClick: () => openClaim(claim),
          title: 'View Claim',
          disabled: actionPending,
        },
      ];

      if (hasEntity) {
        items.push({
          type: 'MenuItem',
          onClick: () => openEntity(claim),
          title: claim.venue ? 'Open Venue' : 'Open Brand',
          disabled: actionPending,
        });
      } else {
        items.push({
          type: 'MenuItem',
          onClick: () =>
            navigate(`/business-claims/${claim.id}/import`, {
              state: { claim },
            }),
          title: 'Import Business',
          disabled: actionPending,
        });
      }

      items.push({
        type: 'Divider',
      });

      if (!hasUser) {
        items.push({
          type: 'MenuItem',
          onClick: () => updateClaim(claim, 'create-user'),
          title: 'Create User + Send Setup Email',
          disabled: actionPending,
        });
      } else {
        items.push({
          type: 'MenuItem',
          onClick: () => updateClaim(claim, 'resend-account-setup'),
          title: 'Resend Account Setup Email',
          disabled: actionPending,
        });
      }

      if (!claim.email_verified_at) {
        items.push({
          type: 'MenuItem',
          onClick: () => updateClaim(claim, 'resend-verification'),
          title: 'Resend Email Verification',
          disabled: actionPending,
        });
      }

      items.push({
        type: 'Divider',
      });

      if (!isApproved && !isRejected) {
        items.push(
          {
            type: 'MenuItem',
            onClick: () => updateClaim(claim, 'review'),
            title: 'Mark Under Review',
            disabled: actionPending,
          },
          {
            type: 'MenuItem',
            onClick: () => updateClaim(claim, 'verify'),
            title: 'Verify Ownership',
            disabled: actionPending || !hasEntity || !hasUser,
          },
          {
            type: 'MenuItem',
            onClick: () => updateClaim(claim, 'approve'),
            title: 'Approve Claim',
            disabled: actionPending || !hasEntity || !hasUser,
          },
          {
            type: 'MenuItem',
            onClick: () => updateClaim(claim, 'reject'),
            style: { color: '#ff4700' },
            title: 'Reject Claim',
            disabled: actionPending,
          }
        );
      }

      return (
        <RowMoreOptions
          row={index}
          items={items}
          onShow={() => {
            document
              .querySelector(`#dropdown-actions-${claim.id}`)
              ?.classList.add('d-none');
          }}
        />
      );
    },
    [actionClaimId, navigate, openClaim, openEntity, updateClaim]
  );

  const editButtonFormatter = useCallback(
    (_cell: unknown, claim: BusinessClaim, index: number) => (
      <div style={{ minWidth: 68 }}>
        <div
          id={`dropdown-actions-${claim.id}`}
          className="dropdown-actions d-none"
        >
          <Icon
            name="edit2"
            className="ml-1 mr-3"
            style={{
              cursor: 'pointer',
              display: 'inline',
              height: 18,
              width: 18,
            }}
            onClick={() => openClaim(claim)}
          />

          {actionDropdown(index, claim)}
        </div>
      </div>
    ),
    [actionDropdown, openClaim]
  );

  const columns = useMemo<ColumnDescription<BusinessClaim>[]>(
    () => [
      {
        dataField: 'entity',
        text: 'Business',
        sort: false,
        headerClasses: 'sticky-header',
        formatter: (_cell: unknown, claim: BusinessClaim) => {
          const entity = getClaimEntity(claim);

          return (
            <div style={{ minWidth: 220 }}>
              <div style={{ fontWeight: 600 }}>{entity.name}</div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  marginTop: 4,
                }}
              >
                <Chip size="small" variant="outlined" label={entity.type} />

                {entity.id && <span className="text-muted">#{entity.id}</span>}

                {entity.id && entity.active === false && (
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label="Staged"
                  />
                )}
              </div>
            </div>
          );
        },
      },
      {
        dataField: 'contact_name',
        text: 'Requester',
        sort: true,
        headerClasses: 'sticky-header',
        formatter: (_cell: unknown, claim: BusinessClaim) => (
          <div style={{ minWidth: 180 }}>
            <div style={{ fontWeight: 500 }}>
              {claim.contact_name || claim.owner_name || 'Unknown requester'}
            </div>

            {claim.contact_email && (
              <div className="text-muted">{claim.contact_email}</div>
            )}

            {claim.contact_number && (
              <div className="text-muted">{String(claim.contact_number)}</div>
            )}
          </div>
        ),
      },
      {
        dataField: 'user',
        text: 'Boxpressd Account',
        sort: false,
        headerClasses: 'sticky-header',
        formatter: (_cell: unknown, claim: BusinessClaim) => {
          const userName = getUserName(claim.user);

          if (!claim.user_id && !claim.user) {
            return (
              <div>
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  label="No account"
                />

                {claim.email_verified_at && (
                  <div className="text-muted mt-1">Email verified</div>
                )}
              </div>
            );
          }

          return (
            <div style={{ minWidth: 180 }}>
              <div style={{ fontWeight: 500 }}>
                {userName ?? `User #${claim.user_id}`}
              </div>

              {claim.user?.email && (
                <div className="text-muted">{claim.user.email}</div>
              )}

              <div style={{ marginTop: 4 }}>
                <Chip
                  size="small"
                  color={claim.email_verified_at ? 'success' : 'default'}
                  variant="outlined"
                  label={
                    claim.email_verified_at
                      ? 'Email verified'
                      : 'Verification pending'
                  }
                />
              </div>
            </div>
          );
        },
      },
      {
        dataField: 'status',
        text: 'Status',
        sort: true,
        headerClasses: 'sticky-header',
        formatter: (_cell: unknown, claim: BusinessClaim) => (
          <Chip
            size="small"
            color={getStatusChipColor(claim)}
            label={getStatusLabel(claim)}
          />
        ),
      },
      {
        dataField: 'processing_timestamp',
        text: 'Submitted',
        sort: true,
        headerClasses: 'sticky-header',
        formatter: (value: string) => (
          <span style={{ whiteSpace: 'nowrap' }}>{formatDate(value)}</span>
        ),
      },
      {
        dataField: 'claimed_timestamp',
        text: 'Completed',
        sort: true,
        headerClasses: 'sticky-header',
        formatter: (value: string | null) => (
          <span style={{ whiteSpace: 'nowrap' }}>{formatDate(value)}</span>
        ),
      },
      {
        dataField: 'actions',
        text: '',
        formatter: editButtonFormatter,
        sort: false,
        editable: false,
        headerClasses: 'sticky-header',
      },
    ],
    [editButtonFormatter]
  );

  const handleTableChange = useCallback(
    (
      _type: string,
      {
        page,
        sizePerPage,
        sortField,
        sortOrder,
        searchText,
      }: {
        page?: number;
        sizePerPage?: number;
        sortField?: string;
        sortOrder?: 'asc' | 'desc';
        searchText?: string;
      }
    ) => {
      const nextState: ClaimsTableState = {
        ...tableState,
        page: page ?? tableState.page,
        sizePerPage: sizePerPage ?? tableState.sizePerPage,
        searchText: searchText ?? tableState.searchText,
        sortField: sortField || tableState.sortField,
        sortOrder: sortOrder || tableState.sortOrder,
      };

      setTableState(nextState);

      const nextSearchParams = new URLSearchParams(searchParams);

      if (nextState.searchText) {
        nextSearchParams.set('q', nextState.searchText);
      } else {
        nextSearchParams.delete('q');
      }

      setSearchParams(nextSearchParams, {
        replace: true,
      });
    },
    [searchParams, setSearchParams, tableState]
  );

  const setStatusFilter = useCallback(
    (status?: string) => {
      const nextState = {
        ...tableState,
        page: 1,
        status,
      };

      setTableState(nextState);

      const nextSearchParams = new URLSearchParams(searchParams);

      if (status) {
        nextSearchParams.set('status', status);
      } else {
        nextSearchParams.delete('status');
      }

      setSearchParams(nextSearchParams, {
        replace: true,
      });
    },
    [searchParams, setSearchParams, tableState]
  );

  const rowEvents = {
    onMouseEnter: (_event: React.MouseEvent, claim: BusinessClaim) => {
      document
        .querySelector(`#dropdown-actions-${claim.id}`)
        ?.classList.remove('d-none');
    },
    onMouseLeave: (_event: React.MouseEvent, claim: BusinessClaim) => {
      document
        .querySelector(`#dropdown-actions-${claim.id}`)
        ?.classList.add('d-none');
    },
    onDoubleClick: (_event: React.MouseEvent, claim: BusinessClaim) => {
      openClaim(claim);
    },
  };

  const paginationOptions = {
    custom: true,
    page: tableState.page,
    sizePerPage: tableState.sizePerPage,
    totalSize: totalClaimCount,
    showTotal: true,
  };

  return (
    <div>
      <Breadcrumb title="Business Claims" label="Manager" parent="Businesses" />

      <div className="container-fluid">
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            marginTop: 16,
          }}
        >
          <Chip
            clickable
            label="All"
            color={!tableState.status ? 'primary' : 'default'}
            variant={!tableState.status ? 'filled' : 'outlined'}
            onClick={() => setStatusFilter(undefined)}
          />

          <Chip
            clickable
            label="Submitted"
            color={tableState.status === 'submitted' ? 'primary' : 'default'}
            variant={tableState.status === 'submitted' ? 'filled' : 'outlined'}
            onClick={() => setStatusFilter('submitted')}
          />

          <Chip
            clickable
            label="Under Review"
            color={tableState.status === 'under_review' ? 'primary' : 'default'}
            variant={
              tableState.status === 'under_review' ? 'filled' : 'outlined'
            }
            onClick={() => setStatusFilter('under_review')}
          />

          <Chip
            clickable
            label="Verification Required"
            color={
              tableState.status === 'verification_required'
                ? 'primary'
                : 'default'
            }
            variant={
              tableState.status === 'verification_required'
                ? 'filled'
                : 'outlined'
            }
            onClick={() => setStatusFilter('verification_required')}
          />

          <Chip
            clickable
            label="Approved"
            color={tableState.status === 'approved' ? 'primary' : 'default'}
            variant={tableState.status === 'approved' ? 'filled' : 'outlined'}
            onClick={() => setStatusFilter('approved')}
          />

          <Chip
            clickable
            label="Rejected"
            color={tableState.status === 'rejected' ? 'primary' : 'default'}
            variant={tableState.status === 'rejected' ? 'filled' : 'outlined'}
            onClick={() => setStatusFilter('rejected')}
          />
        </div>

        <div className="row">
          <div className="col-sm-12">
            <div
              className="m-b-10"
              style={
                isMobile
                  ? {
                      background: 'transparent',
                      boxShadow: 'none',
                    }
                  : {}
              }
            >
              <div className="datatable-react">
                <ToolkitProvider
                  keyField="id"
                  data={claims}
                  columns={columns}
                  search
                >
                  {(toolkitProps: any) => (
                    <div>
                      <div
                        style={{
                          display: 'flex',
                          gap: 12,
                          marginTop: 20,
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <DebounceSearchBar
                            {...toolkitProps.searchProps}
                            placeholder="Search businesses, claimants, emails, or phone numbers..."
                          />
                        </div>

                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={refreshClaims}
                          disabled={loading}
                        >
                          Refresh
                        </button>
                      </div>

                      {loadError && (
                        <div role="alert" className="alert alert-danger">
                          Business claims could not be loaded. The results below
                          may be out of date. Please retry.
                        </div>
                      )}

                      <PaginationProvider
                        pagination={paginationFactory(paginationOptions)}
                      >
                        {({ paginationProps, paginationTableProps }: any) => (
                          <div>
                            <BootstrapTable
                              remote={{
                                pagination: true,
                                filter: true,
                                sort: true,
                                cellEdit: false,
                              }}
                              keyField="id"
                              data={claims}
                              columns={columns}
                              rowEvents={rowEvents}
                              defaultSorted={defaultSorted}
                              wrapperClasses="table-responsive"
                              rowClasses="business-claim-row"
                              {...paginationTableProps}
                              onTableChange={handleTableChange}
                              noDataIndication={() =>
                                !loading ? (
                                  <NoDataIndicator message="No business claims found matching this query." />
                                ) : (
                                  <TableLoader />
                                )
                              }
                              {...toolkitProps.baseProps}
                            />

                            <PaginationTotalStandalone {...paginationProps} />

                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                paddingBottom: 20,
                              }}
                            >
                              <div style={{ flex: 1 }}>
                                <StandalonePagination
                                  paginationProps={paginationProps}
                                />
                              </div>

                              <SizePerPageDropdownStandalone
                                {...paginationProps}
                              />
                            </div>
                          </div>
                        )}
                      </PaginationProvider>
                    </div>
                  )}
                </ToolkitProvider>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaimsTable;
